import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectsAPI, tasksAPI, usersAPI, notificationsAPI, reportsAPI, analyticsAPI, calendarAPI, ticketsAPI, excomAPI, meetingsAPI, governanceAPI } from '../services/apiService';
import toast from 'react-hot-toast';

// Excom Hooks
export const useExcomMeetings = (params = {}) => {
    return useQuery({
        queryKey: ['excomMeetings', params],
        queryFn: async () => {
            const response = await excomAPI.getAllMeetings(params);
            return response.data;
        },
    });
};

export const useExcomMeetingDetail = (id) => {
    return useQuery({
        queryKey: ['excomMeeting', id],
        queryFn: async () => {
            const response = await excomAPI.getMeetingDetail(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useCreateExcomMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => excomAPI.createMeeting(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['excomMeetings'] });
            toast.success('Excom meeting registered successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to register meeting');
        }
    });
};

export const useUpdateExcomMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => excomAPI.updateMeeting(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['excomMeetings'] });
            queryClient.invalidateQueries({ queryKey: ['excomMeeting', String(variables.id)] });
            toast.success('Excom meeting updated successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update meeting');
        }
    });
};

export const useDeleteExcomMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => excomAPI.deleteMeeting(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['excomMeetings'] });
            toast.success('Excom meeting deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete meeting');
        }
    });
};

export const useExcomOpenActions = () => {
    return useQuery({
        queryKey: ['excomOpenActions'],
        queryFn: async () => {
            const response = await excomAPI.getOpenActions();
            return response.data.data;
        },
    });
};

// Projects Hooks
export const useProjects = (params = {}) => {
    return useQuery({
        queryKey: ['projects', params],
        queryFn: async () => {
            const response = await projectsAPI.getAll(params);
            return response.data;
        },
    });
};

export const useProject = (id) => {
    return useQuery({
        queryKey: ['project', String(id)],
        queryFn: async () => {
            const response = await projectsAPI.getById(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useCreateProject = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => projectsAPI.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            toast.success('Project created successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create project');
        }
    });
};

export const useUpdateProject = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => projectsAPI.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            queryClient.invalidateQueries({ queryKey: ['project', String(variables.id)] });
            toast.success('Project updated successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update project');
        }
    });
};

export const useDeleteProject = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => projectsAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            toast.success('Project deleted successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete project');
        }
    });
};

export const useProjectComments = (id) => {
    return useQuery({
        queryKey: ['projectComments', id],
        queryFn: async () => {
            const response = await projectsAPI.getComments(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useAddProjectComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, comment }) => projectsAPI.addComment(id, comment),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['projectComments', variables.id] });
            toast.success('Comment added');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to add comment');
        }
    });
};

export const useUploadProjectFile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, formData }) => projectsAPI.uploadFile(id, formData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
            toast.success('File uploaded successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to upload file');
        }
    });
};

export const useDeleteProjectFile = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, fileId }) => projectsAPI.deleteFile(id, fileId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['project', variables.id] });
            toast.success('File deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete file');
        }
    });
};

// Tasks Hooks
export const useTasks = (params = {}) => {
    return useQuery({
        queryKey: ['tasks', params],
        queryFn: async () => {
            const response = await tasksAPI.getAll(params);
            return response.data;
        },
    });
};

export const useTask = (id) => {
    return useQuery({
        queryKey: ['task', String(id)],
        queryFn: async () => {
            const response = await tasksAPI.getById(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useCreateTask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => tasksAPI.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            toast.success('Task created successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create task');
        }
    });
};

export const useUpdateTask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => tasksAPI.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.invalidateQueries({ queryKey: ['task', String(variables.id)] });
            toast.success('Task updated');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update task');
        }
    });
};

export const useDeleteTask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => tasksAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tasks'] });
            toast.success('Task removed');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to remove task');
        }
    });
};

export const useTaskComments = (id) => {
    return useQuery({
        queryKey: ['taskComments', id],
        queryFn: async () => {
            const response = await tasksAPI.getComments(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useAddTaskComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, comment }) => tasksAPI.addComment(id, comment),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['taskComments', variables.id] });
            toast.success('Comment posted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to post comment');
        }
    });
};

export const useAddSubtask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => tasksAPI.addSubtask(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['task', variables.id] });
            toast.success('Subtask added');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to add subtask');
        }
    });
};

export const useUpdateSubtask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ taskId, subtaskId, data }) => tasksAPI.updateSubtask(taskId, subtaskId, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
            toast.success('Subtask updated');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update subtask');
        }
    });
};

export const useDeleteSubtask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ taskId, subtaskId }) => tasksAPI.deleteSubtask(taskId, subtaskId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
            toast.success('Subtask deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete subtask');
        }
    });
};

export const useAddDependency = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => tasksAPI.addDependency(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['task', variables.id] });
            toast.success('Dependency added');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to add dependency');
        }
    });
};

export const useDeleteDependency = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ taskId, dependencyId }) => tasksAPI.deleteDependency(taskId, dependencyId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['task', variables.taskId] });
            toast.success('Dependency removed');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to remove dependency');
        }
    });
};

// Users Hooks
export const useUsers = (params = {}) => {
    return useQuery({
        queryKey: ['users', params],
        queryFn: async () => {
            const response = await usersAPI.getAll(params);
            return response.data;
        },
    });
};

export const useUser = (id) => {
    return useQuery({
        queryKey: ['user', id],
        queryFn: async () => {
            const response = await usersAPI.getById(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useCreateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => usersAPI.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toast.success('User created');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create user');
        }
    });
};

export const useUpdateUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => usersAPI.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
            toast.success('User updated');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update user');
        }
    });
};

export const useDeleteUser = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => usersAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            toast.success('User deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete user');
        }
    });
};

// Notifications Hooks
export const useNotifications = (params = {}) => {
    return useQuery({
        queryKey: ['notifications', params],
        queryFn: async () => {
            const response = await notificationsAPI.getAll(params);
            return response.data;
        },
        refetchInterval: 30000, // Refetch every 30 seconds
    });
};

export const useMarkNotificationAsRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => notificationsAPI.markAsRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
};

export const useMarkAllNotificationsAsRead = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => notificationsAPI.markAllAsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        },
    });
};

// Reports Hooks
export const useDashboard = () => {
    return useQuery({
        queryKey: ['dashboard'],
        queryFn: async () => {
            const response = await reportsAPI.getDashboard();
            return response.data.data;
        },
    });
};

export const useProjectReports = (params = {}) => {
    return useQuery({
        queryKey: ['projectReports', params],
        queryFn: async () => {
            const response = await reportsAPI.getProjects(params);
            return response.data;
        },
    });
};

export const useTaskReports = (params = {}) => {
    return useQuery({
        queryKey: ['taskReports', params],
        queryFn: async () => {
            const response = await reportsAPI.getTasks(params);
            return response.data;
        },
    });
};

// Analytics Hooks
export const useAnalyticsOverview = (params = {}) => {
    return useQuery({
        queryKey: ['analyticsOverview', params],
        queryFn: async () => {
            const response = await analyticsAPI.getOverview(params);
            return response.data.data;
        },
    });
};

export const useAnalyticsUsers = (params = {}) => {
    return useQuery({
        queryKey: ['analyticsUsers', params],
        queryFn: async () => {
            const response = await analyticsAPI.getUsersReport(params);
            return response.data;
        },
    });
};

export const useAnalyticsProjects = (params = {}) => {
    return useQuery({
        queryKey: ['analyticsProjects', params],
        queryFn: async () => {
            const response = await analyticsAPI.getProjectsReport(params);
            return response.data;
        },
    });
};

export const useAnalyticsTasks = (params = {}) => {
    return useQuery({
        queryKey: ['analyticsTasks', params],
        queryFn: async () => {
            const response = await analyticsAPI.getTasksReport(params);
            return response.data;
        },
    });
};

// Calendar Hooks
export const useCalendarEvents = (params = {}) => {
    return useQuery({
        queryKey: ['calendarEvents', params],
        queryFn: async () => {
            const response = await calendarAPI.getEvents(params);
            return response.data.data;
        },
        enabled: !!(params.start && params.end),
    });
};

// Tickets Hooks
export const useTickets = (params = {}) => {
    return useQuery({
        queryKey: ['tickets', params],
        queryFn: async () => {
            const response = await ticketsAPI.getAll(params);
            return response.data;
        },
    });
};

export const useTicket = (id) => {
    return useQuery({
        queryKey: ['ticket', String(id)],
        queryFn: async () => {
            const response = await ticketsAPI.getById(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useCreateTicket = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => ticketsAPI.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
            toast.success('Ticket created successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to create ticket');
        }
    });
};

export const useUpdateTicket = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => ticketsAPI.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
            queryClient.invalidateQueries({ queryKey: ['ticket', String(variables.id)] });
            toast.success('Ticket updated');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update ticket');
        }
    });
};

export const useDeleteTicket = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => ticketsAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tickets'] });
            toast.success('Ticket removed');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to remove ticket');
        }
    });
};

export const useAddTicketComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, comment }) => ticketsAPI.addComment(id, comment),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['ticket', String(variables.id)] });
            toast.success('Comment posted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to post comment');
        }
    });
};

// ICT Meetings Hooks
export const useMeetings = (params = {}) => {
    return useQuery({
        queryKey: ['meetings', params],
        queryFn: async () => {
            const response = await meetingsAPI.getAll(params);
            return response.data;
        },
    });
};

export const useMeeting = (id) => {
    return useQuery({
        queryKey: ['meeting', String(id)],
        queryFn: async () => {
            const response = await meetingsAPI.getById(id);
            return response.data.data;
        },
        enabled: !!id,
    });
};

export const useCreateMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => meetingsAPI.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['meetings'] });
            queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
            toast.success('Meeting registered successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to register meeting');
        }
    });
};

export const useUpdateMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => meetingsAPI.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['meetings'] });
            queryClient.invalidateQueries({ queryKey: ['meeting', String(variables.id)] });
            queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
            toast.success('Meeting updated successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to update meeting');
        }
    });
};

export const useDeleteMeeting = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => meetingsAPI.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['meetings'] });
            queryClient.invalidateQueries({ queryKey: ['calendarEvents'] });
            toast.success('Meeting deleted successfully');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Failed to delete meeting');
        }
    });
};


/* ==========================================================================
   ICT Governance Hub
   ========================================================================== */

export const useGovernanceForms = () => useQuery({
    queryKey: ['governanceForms'],
    queryFn: async () => (await governanceAPI.getForms()).data,
    staleTime: Infinity, // templates are static configuration
});

export const useGovernanceSummary = () => useQuery({
    queryKey: ['governanceSummary'],
    queryFn: async () => (await governanceAPI.getSummary()).data.data,
});

export const useGovernanceRecords = (params = {}) => useQuery({
    queryKey: ['governanceRecords', params],
    queryFn: async () => (await governanceAPI.getRecords(params)).data,
});

export const useGovernanceRecord = (id) => useQuery({
    queryKey: ['governanceRecord', String(id)],
    queryFn: async () => (await governanceAPI.getRecord(id)).data.data,
    enabled: !!id,
});

export const useGovernanceActions = (params = {}) => useQuery({
    queryKey: ['governanceActions', params],
    queryFn: async () => (await governanceAPI.getActions(params)).data.data,
});

const invalidateGovernance = (queryClient) => {
    queryClient.invalidateQueries({ queryKey: ['governanceRecords'] });
    queryClient.invalidateQueries({ queryKey: ['governanceSummary'] });
    queryClient.invalidateQueries({ queryKey: ['governanceActions'] });
};

export const useCreateGovernanceRecord = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (data) => governanceAPI.createRecord(data),
        onSuccess: (res) => {
            invalidateGovernance(queryClient);
            toast.success(`Record ${res.data.data.record_ref} created`);
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Could not create the record');
        },
    });
};

export const useUpdateGovernanceRecord = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => governanceAPI.updateRecord(id, data),
        onSuccess: (_, variables) => {
            invalidateGovernance(queryClient);
            queryClient.invalidateQueries({ queryKey: ['governanceRecord', String(variables.id)] });
            toast.success('Record updated');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Could not update the record');
        },
    });
};

export const useDeleteGovernanceRecord = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => governanceAPI.deleteRecord(id),
        onSuccess: () => {
            invalidateGovernance(queryClient);
            toast.success('Record deleted');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Could not delete the record');
        },
    });
};

export const useDecideGovernanceApproval = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => governanceAPI.decideApproval(id, data),
        onSuccess: (_, variables) => {
            invalidateGovernance(queryClient);
            if (variables.recordId) {
                queryClient.invalidateQueries({ queryKey: ['governanceRecord', String(variables.recordId)] });
            }
            toast.success('Decision recorded');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Could not record the decision');
        },
    });
};

export const useUpdateGovernanceAction = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => governanceAPI.updateAction(id, data),
        onSuccess: (_, variables) => {
            invalidateGovernance(queryClient);
            if (variables.recordId) {
                queryClient.invalidateQueries({ queryKey: ['governanceRecord', String(variables.recordId)] });
            }
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Could not update the action');
        },
    });
};
